import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import propTypes from 'prop-types';
import Spinner from '../spinner/spinner';
import mergeI18n from '../../lib/util/merge-i18n';
import { submitForm } from '../../lib/form';
import formatTimeSlot from '../../lib/formatters/format-time-slot';
import extendPhoneNumbers from '../../lib/formatters/extend-phone-numbers';
import formatPhone from '../../lib/formatters/phone-formatter';
import phoneTypes from '../phone-field/i18n';
import i18n from './i18n';
import Icon from '../icon/icon';
import * as requestProcessor from '../../services/request-processor';
import {
	APPT_STATUS,
	CONFIRMATION_ROUTE,
	LOCATION_TYPES,
	LOG_IN_ROUTE,
	REQUEST_STATUS,
	SCHEDULE_ROUTE
} from '../../constants/constants';

const reviewDefaultState = {
	inspectorComment: '',
	maxLength: 255,
	currentLength: 0
};

class Review extends React.Component {
	constructor (props) {
		super(props);

		this.state = {
			inspectorComment: '',
			maxLength: 255,
			currentLength: 0,
			isLoading: false
		};
		this.onUpdateInspectorComment = this.onUpdateInspectorComment.bind(this);
		this.onBackReview = this.onBackReview.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);
		this.scheduleAppointment = this.scheduleAppointment.bind(this);
		this.setRequestOnHold = this.setRequestOnHold.bind(this);
	}

	componentDidMount () {
		if (_.isEmpty(this.props.inspectionRequest)) {
			this.props.setRoute(LOG_IN_ROUTE);
		} else {
			this.setState({
				inspectorComment: '',
				maxLength: 255,
				currentLength: 0
			});
			this.props.setParentState({
				progressIndex: 4,
				canLogOut: false,
				canGoToHomePage: false
			});
		}
	}

	formatInspectorComment () {
		const lesseeIsPresent = this.props.lesseeIsPresent === 'true';
		const request = this.props.inspectionRequest;
		const first = lesseeIsPresent ? request.lesseeFirstName : this.props.onSiteContactFirstName;
		const last = lesseeIsPresent ? request.lesseeLastName : this.props.onSiteContactLastName;
		const phone = formatPhone(lesseeIsPresent ? this.props.contact.phone : this.props.onSiteContactPhone);
		const phoneType = _.get(
			_.find(
				phoneTypes.EN.phoneTypes,
				{ value: lesseeIsPresent ? this.props.contact.phoneType : this.props.onSiteContactPhoneType }
			),
			'display'
		);

		const inspectionContact = !lesseeIsPresent ? `On-Site Contact: ${first} ${last}, ${phone} (${phoneType})` : null;

		const locationName = this.props.location.name;
		const locationType = this.props.locationType;
		const work = LOCATION_TYPES.EN.find(type => type.value === 'work').display;

		return [
			locationName ?
				`Location: ${locationName} ${locationType && locationType === 'work' ? `(${work})` : ''}` :
				null,
			inspectionContact,
			this.state.inspectorComment
		].filter(part => !!part)
			.map(str => str.trim())
			.join('. ');
	}

	onUpdateInspectorComment (newText, currentLength) {
		this.setState({
			inspectorComment: newText.substring(0, reviewDefaultState.maxLength),
			maxLength: reviewDefaultState.maxLength,
			currentLength: (currentLength > reviewDefaultState.maxLength ? reviewDefaultState.maxLength : currentLength)
		});
	}

	onBackReview () {
		this.props.setRoute(SCHEDULE_ROUTE);
	}

	scheduleAppointment () {
		this.setState({ isLoading: true });

		const token = window.sessionStorage.getItem('token');
		const request = this.props.inspectionRequest;
		const requestId = request.confirmationNo;
		const lesseeIsPresent = this.props.lesseeIsPresent === 'true';

		const appt = {
			status: APPT_STATUS.OPEN,
			startDatetime: this.props.selectedTime.startDatetime,
			endDatetime: this.props.selectedTime.endDatetime,
			isForceCreate: false,
			location: this.props.location,
			contactFirstName: lesseeIsPresent ? request.lesseeFirstName : this.props.onSiteContactFirstName,
			contactLastName: lesseeIsPresent ? request.lesseeLastName : this.props.onSiteContactLastName,
			contactEmail: lesseeIsPresent ? this.props.contact.email : this.props.onSiteContactEmail,
			contactPhone: lesseeIsPresent ? this.props.contact.phone : this.props.onSiteContactPhone,
			contactPreferredMethod: lesseeIsPresent ? this.props.contact.prefContactMethod : this.props.onSiteContactPrefMethod
		};

		if (this.state.inspectorComment) {
			appt.inspectorComment = this.formatInspectorComment();
		}

		const lesseeUpdates = {
			lesseeEmail: this.props.contact.email,
			contactEmail: this.props.contact.email,
			contactPreferredMethod: this.props.contact.prefContactMethod,
			inspectionLocation: this.props.location,
			skipClickSend: true
		};

		requestProcessor.updateRequest(requestId, lesseeUpdates, token, (err) => {
			if (err) {
				this.props.setParentState({ scheduleSucceeded: false });
				this.props.setRoute(CONFIRMATION_ROUTE);
				this.setState({ isLoading: false });
				return;
			}

			requestProcessor.scheduleAppointment(requestId, appt, token, (err) => {
				this.setState({ isLoading: false });

				if (err) {
					this.props.setParentState({ scheduleSucceeded: false });
					this.props.setRoute(CONFIRMATION_ROUTE);
					return;
				}

				this.props.setParentState({ scheduleSucceeded: true });
				this.props.setRoute(CONFIRMATION_ROUTE);
			});
		});
	}

	setRequestOnHold () {
		this.setState({ isLoading: true });

		const token = window.sessionStorage.getItem('token');
		const request = this.props.inspectionRequest;
		const requestId = request.confirmationNo;
		const lesseeIsPresent = this.props.lesseeIsPresent === 'true';

		requestProcessor.getReasons(token, (err, res) => {
			const reasonId = _.get(_.find(_.get(res, `body.results[${REQUEST_STATUS.ON_HOLD}]`), { reason: 'Awaiting Dispatch' }), 'reasonId');

			if (err || !reasonId) {
				this.props.setParentState({ scheduleSucceeded: false });
				this.props.setRoute(CONFIRMATION_ROUTE);
				this.setState({ isLoading: false });
				return;
			}

			let requestUpdates;
			if (lesseeIsPresent) {
				requestUpdates = {
					phoneNumbers: extendPhoneNumbers(
						request.phoneNumbers,
						this.props.contact.phone,
						['contact']
					),
					inspectionLocation: this.props.location,
					lesseeEmail: this.props.contact.email,
					contactPreferredMethod: this.props.contact.prefContactMethod
				};
			} else {
				requestUpdates = {
					phoneNumbers: extendPhoneNumbers(
						request.phoneNumbers,
						this.props.onSiteContactPhone,
						['contact']
					),
					inspectionLocation: this.props.location,
					contactFirstName: this.props.onSiteContactFirstName,
					contactLastName: this.props.onSiteContactLastName,
					contactPreferredMethod: this.props.onSiteContactPrefMethod,
					contactEmail: this.props.onSiteContactEmail,
					lesseeEmail: this.props.contact.email
				};
			}

			if (this.state.inspectorComment) {
				requestUpdates.inspectorComment = this.formatInspectorComment();
			}

			const date = moment.utc(new Date(this.props.selectedTime.startDatetime)).format('MM-DD-YYYY');
			const start = moment.utc(new Date(this.props.selectedTime.startDatetime)).format('h:mm A');
			const end = moment.utc(new Date(this.props.selectedTime.endDatetime)).format('h:mm A');
			const comment = `Lessee Self Schedule - Requested appointment on ${date} from ${start} to ${end}`;

			requestProcessor.updateStatus(requestId, REQUEST_STATUS.ON_HOLD, reasonId, token, (err) => {
				if (err) {
					this.props.setParentState({ scheduleSucceeded: false });
					this.props.setRoute(CONFIRMATION_ROUTE);
					this.setState({ isLoading: false });
					return;
				}

				requestProcessor.updateRequest(requestId, requestUpdates, token, (err) => {
					if (err) {
						this.props.setParentState({ scheduleSucceeded: false });
						this.props.setRoute(CONFIRMATION_ROUTE);
						this.setState({ isLoading: false });
						return;
					}

					requestProcessor.addComment(requestId, comment, token, (err) => {
						this.setState({ isLoading: false });

						if (err) {
							this.props.setParentState({ scheduleSucceeded: false });
							this.props.setRoute(CONFIRMATION_ROUTE);
							return;
						}

						this.props.setParentState({ scheduleSucceeded: true });
						this.props.setRoute(CONFIRMATION_ROUTE);
					});
				});
			});
		});
	}

	handleSubmit () {
		if (this.props.usingClick) {
			this.scheduleAppointment();
		} else {
			this.setRequestOnHold();
		}
	}

	render () {
		// Dispite changing the route in componentDidMount React will attempt to render this
		// at least once, so we're doing this to avoid a crash
		if (_.isEmpty(this.props.inspectionRequest)) {
			return (<div />);
		}

		if (this.state.isLoading) {
			return (<Spinner />);
		}

		const __ = mergeI18n(this.props.language, i18n);
		document.title = `${__.titleStem} - ${__.pageTitle}`;

		const profileRow = (icon, rows) => (
			<div className='row profile-row'>
				<div className='col-xs-2'>
					<Icon name={icon} />
				</div>
				<div className='col-xs-10 text-col'>
					{rows}
				</div>
			</div>
		);

		const staticProfileRow = (icon, lines) => profileRow(icon, lines.map((line, index) => (
			<p id={line.id} key={`profile-row-${icon}-${index}`}>{line.display}</p>
		)));

		const updateInspectorComment = ev => this.onUpdateInspectorComment(ev.target.value, ev.target.value.length);

		const contactLines = [{
			display: `${this.props.inspectionRequest.lesseeFirstName || ''} ${this.props.inspectionRequest.lesseeLastName || ''}`.trim(),
			id: 'lesseeName'
		}, {
			display: this.props.contact.prefContactMethod === 'email' ? this.props.contact.email : formatPhone(this.props.contact.phone), id: 'prefContactMethod'
		}];

		if (this.props.lesseeIsPresent !== 'true') {
			contactLines.push({ display: `${this.props.onSiteContactFirstName} ${this.props.onSiteContactLastName}`, id: 'onSiteContactName' });
			contactLines.push({
				display: this.props.onSiteContactPrefMethod === 'email' ?
					this.props.onSiteContactEmail :
					formatPhone(this.props.onSiteContactPhone),
				id: 'onSiteContactPrefMethod'
			});
		}

		const vehicle = this.props.inspectionRequest.vehicles[0];

		return (
			<div id='lss-content' className='container'>
				<div className='row'>
					<div className='col-xs-12'>
						<div id='review'>
							<form onSubmit={submitForm(true, this.handleSubmit)}>
								<h1>{__.title(this.props.usingClick)}</h1>
								<h2>{__.instructions(this.props.usingClick)}</h2>
								<div className='row'>
									<div className='col-xs-12 col-sm-6 col-md-5'>
										{staticProfileRow('MdDirectionsCar', [{ display: __.formatting.vehicleYmm(vehicle), id: 'vehicleYmm' }, { display: __.formatting.vehicleVin(vehicle), id: 'vehicleVin' }])}
										{staticProfileRow('MdToday', [{ display: __.formatDate(this.props.selectedDate), id: 'selectedDate' }])}
										{staticProfileRow('MdAccessTime', [{ display: formatTimeSlot(this.props.selectedTime).display, id: 'selectedTime' }])}
										{staticProfileRow('MdLocationOn', [{ display: __.formatting.location(this.props.location), id: 'location' }])}
									</div>
									<div className='col-xs-12 col-sm-6 col-md-5 col-md-offset-1'>
										{staticProfileRow('MdAccountBox', contactLines)}
										{profileRow(
											'MdMessage', (
												<div className='textarea-counter'>
													<textarea
														id='inspectorComment'
														maxLength={this.state.maxLength}
														placeholder={__.inspectorComment}
														value={this.state.inspectorComment}
														onChange={updateInspectorComment}
													/>
													<span>{this.state.currentLength}/{this.state.maxLength}</span>
												</div>
											)
										)}
									</div>
								</div>
								<div className='flex-button-spacer' />
								<div className='form-buttons-wrapper'>
									<div className='form-buttons'>
										<button id='back-button' className='btn btn-default back-button' type='button' onClick={this.onBackReview}><Icon name='MdChevronLeft' /> {__.backButton}</button>
										<button className='btn btn-primary' type='submit'>{__.confirmAppointment(this.props.usingClick)}</button>
									</div>
								</div>
							</form>
						</div>
					</div>
				</div>
			</div>
		);
	}
}

Review.propTypes = {
	inspectionRequest: propTypes.shape({
		confirmationNo: propTypes.number,
		clickTaskType: propTypes.string,
		lesseeFirstName: propTypes.string,
		lesseeLastName: propTypes.string,
		lesseeEmail: propTypes.string,
		vehicles: propTypes.arrayOf(propTypes.shape({
			vin: propTypes.string.isRequired,
			make: propTypes.string,
			model: propTypes.string,
			year: propTypes.string
		}))
	}).isRequired,
	contact: propTypes.shape({
		phone: propTypes.string,
		email: propTypes.string,
		prefContactMethod: propTypes.string
	}).isRequired,
	locationType: propTypes.string,
	location: propTypes.shape({
		name: propTypes.string,
		country: propTypes.string,
		street1: propTypes.string,
		street2: propTypes.string,
		city: propTypes.string,
		state: propTypes.string,
		zip: propTypes.string
	}).isRequired,
	onSiteContactEmail: propTypes.string,
	onSiteContactPhone: propTypes.string,
	onSiteContactPhoneType: propTypes.string,
	onSiteContactFirstName: propTypes.string,
	onSiteContactLastName: propTypes.string,
	onSiteContactPrefMethod: propTypes.string,
	usingClick: propTypes.bool.isRequired,
	lesseeIsPresent: propTypes.string.isRequired,
	language: propTypes.string.isRequired,
	setParentState: propTypes.func.isRequired,
	setRoute: propTypes.func.isRequired
};
export default Review;
